import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import WarningOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TextInput from 'v3/Components/Form/TextInput';
import FileUploadBox, { UploadedFile } from 'v3/Components/Form/FileUploadBox';
import { PrimaryButton, TertiaryButton, DangerButton } from 'v3/Components/Shared/Buttons';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { buildEndpoint, useApiWorker, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { useGlobalState, useToasts } from 'Context';
import { ENDPOINTS, METHODS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import './ReviewerProfile.Documents.Drawer.scss';

// Non-selectable document type IDs per ticket spec
const NON_SELECTABLE_TYPE_IDS = [3, 4]; // Board Certification (3), State License (4)

type DocumentTypeModel = {
  id: number;
  name: string;
  color: string;
  isInternalOnly: boolean;
};

export type ReviewerDocumentRow = {
  id: string;
  title: string;
  fileName: string;
  format: string;
  uploadDate: string;
  uploadedBy: string;
  documentType: DocumentTypeModel;
};

type Props = {
  mode: 'upload' | 'edit';
  reviewerId: string;
  document?: ReviewerDocumentRow;
  documentTypes: DocumentTypeModel[];
  open: boolean;
  onClose: () => void;
  onSuccess: (doc: ReviewerDocumentRow) => void;
  onDelete?: (id: string) => void;
};

type FormErrors = {
  file?: string;
  documentTypeId?: string;
  title?: string;
};

const GroupedTypeSelect: React.FC<{
  value: number;
  onChange: (id: number) => void;
  types: DocumentTypeModel[];
  error?: string;
  disabled?: boolean;
}> = ({ value, onChange, types, error, disabled }) => {
  const visible = types.filter(t => !t.isInternalOnly);
  const internal = types.filter(t => t.isInternalOnly);

  return (
    <div className="doc-drawer__field">
      <label className={`doc-drawer__label${error ? ' doc-drawer__label--error' : ''}`}>
        Type <span className="required">*</span>
      </label>
      <select
        className={`doc-drawer__select${error ? ' doc-drawer__select--error' : ''}`}
        value={value || ''}
        onChange={e => onChange(Number(e.target.value))}
        disabled={disabled}
      >
        <option value="">--</option>
        <optgroup label="Visible by All Users">
          {visible.map(t => (
            <option
              key={t.id}
              value={t.id}
              disabled={NON_SELECTABLE_TYPE_IDS.includes(t.id)}
            >
              {t.name}{NON_SELECTABLE_TYPE_IDS.includes(t.id) ? ' (managed separately)' : ''}
            </option>
          ))}
        </optgroup>
        {internal.length > 0 && (
          <optgroup label="Internal Only">
            {internal.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {error && (
        <div className="doc-drawer__field-error">
          <WarningOutlinedIcon className="doc-drawer__field-error-icon" />
          {error}
        </div>
      )}
    </div>
  );
};

export const DocumentDrawer: React.FC<Props> = ({
  mode,
  reviewerId,
  document,
  documentTypes,
  open,
  onClose,
  onSuccess,
  onDelete,
}) => {
  const toast = useToasts();
  const { baseUrl } = useGlobalState();
  const hasPermission = useHasPermissionsV3();
  const canDelete = hasPermission(PermissionCodesV3.Reviewer.Document.Delete);

  const isEdit = mode === 'edit';

  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [documentTypeId, setDocumentTypeId] = useState<number>(0);
  const [title, setTitle] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [showBanner, setShowBanner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const uploadEndpoint = `${baseUrl}api/${buildEndpoint(ENDPOINTS.reviewerDocuments.base, METHODS.Upload)}`;

  const API = useApiWorker('3.0');

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setShowBanner(false);
    if (isEdit && document) {
      setDocumentTypeId(document.documentType?.id ?? 0);
      setTitle(document.title ?? '');
      setUploadedFile(
        document.fileName
          ? { serverId: '', filename: document.fileName, fileSize: 0 }
          : null
      );
    } else {
      setDocumentTypeId(0);
      setTitle('');
      setUploadedFile(null);
    }
  }, [open, document, isEdit]);

  const validate = (): FormErrors => {
    const e: FormErrors = {};
    // Clearing the file in edit mode must not silently save; deleting uses the dedicated action.
    if (!uploadedFile) e.file = isEdit ? 'Please upload a document before saving changes' : 'This is required';
    if (!documentTypeId) e.documentTypeId = 'This is required';
    if (!title.trim()) e.title = 'This is required';
    return e;
  };

  const handleSave = async () => {
    if (isUploading) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setShowBanner(true);
      return;
    }
    setErrors({});
    setShowBanner(false);
    setSaving(true);
    try {
      let result: any;
      if (isEdit) {
        const payload: any = {
          documentTypeId,
          title: title.trim(),
        };
        if (uploadedFile?.serverId) {
          payload.fileId = uploadedFile.serverId;
          payload.fileName = uploadedFile.filename;
          payload.size = uploadedFile.fileSize;
        }
        result = await API.put(
          buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'documents', document!.id),
          payload
        );
      } else {
        result = await API.post(
          buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'documents'),
          {
            fileId: uploadedFile!.serverId,
            fileName: uploadedFile!.filename,
            size: uploadedFile!.fileSize,
            documentTypeId,
            title: title.trim(),
          }
        );
      }
      if (result?.status >= 200 && result?.status < 300) {
        if (isEdit) toast.success('Changes were saved successfully');
        onSuccess(result.data as ReviewerDocumentRow);
      } else {
        toast.error('An error occurred. Please try again.');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      const result: any = await API.delete(
        buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'documents', document!.id)
      );
      if (result?.status >= 200 && result?.status < 300) {
        toast.success('Document was deleted successfully');
        setDeleteModalOpen(false);
        onDelete?.(document!.id);
      } else {
        toast.error('An error occurred. Please try again.');
      }
    } catch {
      toast.error('An error occurred. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const drawerTitle = isEdit ? 'Edit Document' : 'Upload Document';

  const footer = (
    <div className="doc-drawer__footer">
      <TertiaryButton title="Cancel" onClick={onClose} />
      <PrimaryButton
        title={isEdit ? 'Save' : 'Add'}
        iconPosition="start"
        onClick={handleSave}
        disabled={saving || isUploading}
      >
        {saving
          ? <CircularProgress size={16} thickness={5} style={{ color: 'inherit' }} />
          : isEdit && <SaveOutlinedIcon />
        }
      </PrimaryButton>
    </div>
  );

  return (
    <>
      <Drawer
        visible={open}
        onClose={onClose}
        title={<span className="doc-drawer__title">{drawerTitle}</span>}
        width={640}
        closable
        closeIcon={<CloseIcon />}
        footer={footer}
        destroyOnClose
        className="doc-drawer"
        keyboard={!deleteModalOpen}
        maskClosable={!deleteModalOpen}
      >
        <div className="doc-drawer__body">
          {showBanner && (
            <div className="doc-drawer__banner">
              <WarningOutlinedIcon className="doc-drawer__banner-icon" />
              <span>Missing Required Fields — please complete all required fields before saving.</span>
            </div>
          )}

          {/* File upload — required in both modes; in edit mode it may be replaced but not cleared */}
          <div className={`doc-drawer__field${errors.file ? ' doc-drawer__field--error' : ''}`}>
            <FileUploadBox
              uploadEndpoint={uploadEndpoint}
              initialFile={
                isEdit && document?.fileName
                  ? { serverId: '', filename: document.fileName, fileSize: 0 }
                  : undefined
              }
              label="File *"
              onChange={(f) => {
                setUploadedFile(f);
                if (f) {
                  setErrors(prev => ({ ...prev, file: undefined }));
                  setTitle(prev => prev.trim() ? prev : f.filename.replace(/\.[^/.]+$/, ''));
                }
              }}
              onUploadStateChange={(s) => setIsUploading(s === 'uploading')}
            />
            {errors.file && (
              <div className="doc-drawer__field-error">
                <WarningOutlinedIcon className="doc-drawer__field-error-icon" />
                {errors.file}
              </div>
            )}
          </div>

          {/* Type dropdown — locked when editing a document linked to a board certification/state license */}
          <GroupedTypeSelect
            value={documentTypeId}
            onChange={(id) => {
              setDocumentTypeId(id);
              if (id) setErrors(prev => ({ ...prev, documentTypeId: undefined }));
            }}
            types={documentTypes}
            error={errors.documentTypeId}
            disabled={isEdit && NON_SELECTABLE_TYPE_IDS.includes(documentTypeId)}
          />

          {/* Title */}
          <TextInput
            label="Title"
            required
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (e.target.value.trim()) setErrors(prev => ({ ...prev, title: undefined }));
            }}
            error={errors.title}
          />

          {/* Delete link — edit mode, admin only */}
          {isEdit && canDelete && (
            <div className="doc-drawer__delete-section">
              <hr className="doc-drawer__divider" />
              <button
                className="doc-drawer__delete-link"
                onClick={() => setDeleteModalOpen(true)}
              >
                <CloseIcon className="doc-drawer__delete-icon" />
                Delete Document
              </button>
            </div>
          )}
        </div>
      </Drawer>

      <ConfirmDialog
        dialogVisible={deleteModalOpen}
        toggleDialog={() => setDeleteModalOpen(false)}
        title="Delete Document"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        isPrimaryDanger
        primaryButtonEnabled={!deleting}
        OnPrimaryButtonClick={handleConfirmDelete}
        OnSecondaryButtonClick={() => setDeleteModalOpen(false)}
      >
        <div className="doc-drawer__delete-modal-body">
          Are you sure you want to delete this reviewer's document? This will remove the document
          permanently from the system.
        </div>
      </ConfirmDialog>
    </>
  );
};
